import { useEffect, useState } from 'react';
import type { ContactListItem, QuotationPreviewResult } from '@neuroedge/shared';
import { channelLabel } from '@neuroedge/shared';
import {
  Button,
  Card,
  FileDropzone,
  Modal,
  Select,
  TextField,
  useToast,
} from '../../components/ui';
import { useContacts } from '../../api/contacts';
import { useAuth } from '../../auth/AuthContext';
import { QuickAddContact } from './QuickAddContact';
import { useChannels } from '../../api/channels';
import {
  useCreateQuotationGenerationJob,
  useGenerateQuotation,
  usePreviewManualQuotationFromText,
  useQuotationGenerationJob,
  useSendQuotation,
} from '../../api/quotations';
import { QuotationItemsEditor } from './QuotationItemsEditor';
import { rowsFromPreview, toItems, validateRows, type EditorRow } from './quotation-editor-model';

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.pdf'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

function isAllowedFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Staff-generated quotation popup: a walk-in/offline customer's paper
 * list, photographed or typed in, instead of the customer sending it over
 * WhatsApp themselves. Same upload-then-preview-then-confirm shape as
 * `ImportModal`/`AiCatalogImportModal` - a modal, not a standalone page,
 * using the shared `FileDropzone` control. Three steps: pick the contact +
 * an input (photo/PDF or typed list) -> "Generate preview" (AI extraction
 * + catalog match, not persisted) -> resolve any ambiguous rows and
 * "Create" (persists, not sent) -> "Send" (resolves/creates the WhatsApp
 * conversation and sends the PDF) - the last two are split so a quotation
 * can be created now and sent later, or re-sent if a send attempt fails.
 */
export function NewQuotationModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();

  const [contactSearch, setContactSearch] = useState('');
  const [contact, setContact] = useState<ContactListItem | null>(null);
  const contacts = useContacts({ search: contactSearch.trim() || undefined, pageSize: 10 });
  const { hasPermission } = useAuth();
  const canAddContact = hasPermission('contacts.create');
  const [addingContact, setAddingContact] = useState(false);

  const channels = useChannels({ type: 'WHATSAPP' });
  const whatsappChannels = channels.data ?? [];
  const [channelId, setChannelId] = useState('');
  useEffect(() => {
    if (whatsappChannels.length === 1 && !channelId) setChannelId(whatsappChannels[0]!.id);
  }, [whatsappChannels, channelId]);

  const [mode, setMode] = useState<'file' | 'text'>('file');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [itemsText, setItemsText] = useState('');

  const [rows, setRows] = useState<EditorRow[] | null>(null);
  const [currency, setCurrency] = useState('USD');
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  function loadPreview(preview: QuotationPreviewResult) {
    setRows(rowsFromPreview(preview));
    setCurrency(preview.currency);
    setRowErrors({});
  }
  const [resultWarning, setResultWarning] = useState<string | null>(null);
  const [createdQuotationId, setCreatedQuotationId] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Background multi-file extraction job: the file branch no longer blocks
  // on AI extraction - it returns a job immediately, and this component
  // polls it (`useQuotationGenerationJob`) until it reaches a terminal
  // state. The job keeps running server-side even if the user closes this
  // modal (the "ongoing quotations" panel on the Quotations page, plus a
  // notification once it's done, cover that case).
  const [jobId, setJobId] = useState<string | null>(null);
  const createJob = useCreateQuotationGenerationJob();
  const job = useQuotationGenerationJob(jobId ?? undefined);

  const previewFromText = usePreviewManualQuotationFromText();
  const generate = useGenerateQuotation();
  const send = useSendQuotation();

  useEffect(() => {
    if (!job.data) return;
    if (job.data.status === 'DONE' && job.data.result) {
      const result = job.data.result;
      if (result.matched.length + result.unmatched.length + result.ambiguous.length === 0) {
        toast.error(
          'No items could be read from these files. Try clearer photos, or type the list instead.',
        );
      } else {
        loadPreview(result);
        setResultWarning(job.data.warningMessage);
      }
      setJobId(null);
    } else if (job.data.status === 'FAILED') {
      toast.error(job.data.errorMessage ?? 'Could not process these files.');
      setJobId(null);
    }
  }, [job.data, toast]);

  function pickFiles(candidates: File[]) {
    setFileError(null);
    if (candidates.length > MAX_FILES) {
      setFileError(`Up to ${MAX_FILES} files at once.`);
      setFiles(candidates.slice(0, MAX_FILES));
      return;
    }
    const bad = candidates.find((f) => !isAllowedFile(f));
    if (bad) {
      setFileError(`"${bad.name}" isn't an image (jpg/png/webp/gif) or a PDF.`);
      return;
    }
    const tooBig = candidates.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setFileError(`"${tooBig.name}" is larger than 10MB.`);
      return;
    }
    setFiles(candidates);
  }

  async function runPreview() {
    if (!contact) {
      toast.error('Pick a contact first.');
      return;
    }
    if (mode === 'file') {
      if (files.length === 0) {
        toast.error('Choose one or more photos or PDFs first.');
        return;
      }
      try {
        const createdJob = await createJob.mutateAsync({ contactId: contact.id, files });
        setJobId(createdJob.id);
        toast.success("Processing in the background - we'll show progress below.");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not start processing these files.');
      }
      return;
    }
    if (!itemsText.trim()) {
      toast.error('Type an item list first.');
      return;
    }
    try {
      const preview = await previewFromText.mutateAsync({
        contactId: contact.id,
        itemsText: itemsText.trim(),
      });
      if (preview.matched.length + preview.unmatched.length + preview.ambiguous.length === 0) {
        toast.error(
          'No items could be read from this. Try a clearer photo, or type the list instead.',
        );
        return;
      }
      loadPreview(preview);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read this item list.');
    }
  }

  async function createQuotation() {
    if (!contact || !rows) return;
    const found = validateRows(rows);
    setRowErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    try {
      const quotation = await generate.mutateAsync({
        contactId: contact.id,
        items: toItems(rows),
      });
      setCreatedQuotationId(quotation.id);
      toast.success('Quotation created.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the quotation.');
    }
  }

  async function sendQuotationNow() {
    if (!createdQuotationId || !channelId) return;
    try {
      await send.mutateAsync({ quotationId: createdQuotationId, input: { channelId } });
      setSent(true);
      toast.success('Quotation sent.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not send the quotation.');
    }
  }

  const unresolvedAmbiguousCount = (rows ?? []).filter((r) => r.needsPick).length;

  if (createdQuotationId) {
    return (
      <Modal title="Quotation created" onClose={onClose}>
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <p style={{ margin: 0 }}>
            The quotation was created for <strong>{contact?.displayName}</strong>. Send it over
            WhatsApp now, or send it later from the Quotations list.
          </p>
          {whatsappChannels.length > 1 ? (
            <Select
              label="Send from channel"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              options={[
                { value: '', label: 'Choose a channel…' },
                ...whatsappChannels.map((c) => ({ value: c.id, label: channelLabel(c) })),
              ]}
            />
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {!sent ? (
              <Button
                onClick={() => void sendQuotationNow()}
                disabled={!channelId || send.isPending}
              >
                {send.isPending ? 'Sending…' : 'Send over WhatsApp'}
              </Button>
            ) : (
              <span className="ne-badge">Sent</span>
            )}
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="New quotation" onClose={onClose} wide maxWidth={980}>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        For a walk-in or offline customer - photograph or type their item list yourself, then share
        the resulting quotation with them over WhatsApp.
      </p>

      <Card title="1. Customer">
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {contact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span>
                <strong>{contact.displayName}</strong>
                {contact.phone ? ` — ${contact.phone}` : ''}
              </span>
              <Button
                variant="secondary"
                onClick={() => {
                  setContact(null);
                  setRows(null);
                  setFiles([]);
                  setJobId(null);
                }}
              >
                Change
              </Button>
            </div>
          ) : addingContact ? (
            <QuickAddContact
              initialText={contactSearch}
              onAdded={(c) => {
                setContact(c);
                setContactSearch('');
                setAddingContact(false);
              }}
              onCancel={() => setAddingContact(false)}
            />
          ) : (
            <>
              <TextField
                label="Search contacts"
                placeholder="Name, phone, or email"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
              />
              {contactSearch.trim() && contacts.data ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                  {contacts.data.items.length === 0 ? (
                    <li
                      style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}
                    >
                      No contacts match.{canAddContact ? ' Add them as a new customer below.' : ''}
                    </li>
                  ) : (
                    contacts.data.items.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setContact(c);
                            setContactSearch('');
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--space-2)',
                            cursor: 'pointer',
                          }}
                        >
                          {c.displayName} {c.phone ? `— ${c.phone}` : ''}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
              {canAddContact ? (
                <div>
                  <Button variant="secondary" onClick={() => setAddingContact(true)}>
                    + New customer
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </Card>

      <Card title="2. Item list">
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant={mode === 'file' ? 'primary' : 'secondary'}
              onClick={() => setMode('file')}
            >
              Photo / PDF
            </Button>
            <Button
              variant={mode === 'text' ? 'primary' : 'secondary'}
              onClick={() => setMode('text')}
            >
              Type the list
            </Button>
          </div>

          {mode === 'file' ? (
            <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <FileDropzone
                accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
                multiple
                maxFiles={MAX_FILES}
                files={files}
                onFilesSelected={pickFiles}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Up to {MAX_FILES} photos/PDFs at once - they're processed in the background, so you
                can close this and keep working; you'll be notified once the preview is ready.
              </p>
              {fileError ? (
                <p style={{ margin: 0, color: 'var(--color-danger)' }}>{fileError}</p>
              ) : null}
            </div>
          ) : (
            <textarea
              rows={6}
              value={itemsText}
              onChange={(e) => setItemsText(e.target.value)}
              placeholder="e.g. UPVC pipe 1 inch x 5, CPVC elbow x 10..."
              style={{
                width: '100%',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                font: 'inherit',
              }}
            />
          )}

          {jobId &&
          job.data &&
          (job.data.status === 'PENDING' || job.data.status === 'PROCESSING') ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <div
                style={{
                  height: 8,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-border)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((job.data.processedFiles / Math.max(job.data.totalFiles, 1)) * 100)}%`,
                    background: 'var(--color-primary)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Reading file {Math.min(job.data.processedFiles + 1, job.data.totalFiles)} of{' '}
                {job.data.totalFiles}…
              </p>
            </div>
          ) : (
            <Button
              onClick={() => void runPreview()}
              disabled={!contact || createJob.isPending || previewFromText.isPending}
            >
              {createJob.isPending || previewFromText.isPending ? 'Starting…' : 'Generate preview'}
            </Button>
          )}
        </div>
      </Card>

      {resultWarning && rows ? (
        <p style={{ margin: 0, color: 'var(--color-warning, #b45309)' }}>{resultWarning}</p>
      ) : null}

      {rows ? (
        <Card title="3. Review, price & discount">
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <QuotationItemsEditor
              rows={rows}
              onChange={(next) => {
                setRows(next);
                if (Object.keys(rowErrors).length > 0) setRowErrors({});
              }}
              currency={currency}
              errors={rowErrors}
            />
            {unresolvedAmbiguousCount > 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {unresolvedAmbiguousCount} item(s) still need a match picked above (or leave them
                unmatched on purpose).
              </p>
            ) : null}
            <Button onClick={() => void createQuotation()} disabled={generate.isPending}>
              {generate.isPending ? 'Creating…' : 'Create quotation'}
            </Button>
          </div>
        </Card>
      ) : null}
    </Modal>
  );
}
